import { useState, useEffect } from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Award, GraduationCap, Tv, Newspaper, Heart, Building2, Stethoscope, AlertTriangle, TrendingUp, Image } from 'lucide-react';
import api from '@/utils/api';

const categories = [
  { id: 'awards', label: 'Awards / Appreciation', icon: Award },
  { id: 'career', label: 'Career Guidelines', icon: GraduationCap },
  { id: 'media', label: 'Media', icon: Tv },
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'education', label: 'Education & Support', icon: Heart },
  { id: 'csr', label: 'CSR Projects', icon: Building2 },
  { id: 'health', label: 'Health', icon: Stethoscope },
  { id: 'disaster', label: 'Disaster Management', icon: AlertTriangle },
];

const yearlyStats = [
  { year: '2016-2018', posted: '24,508', placed: '3,436', rate: '7%' },
  { year: '2019', posted: '1,39,330', placed: '26,901', rate: '20%' },
  { year: '2020', posted: '1,99,555', placed: '29,326', rate: '14%' },
  { year: '2021', posted: '1,99,020', placed: '33,413', rate: '17%' },
  { year: '2022', posted: '2,32,666', placed: '32,361', rate: '14%' },
  { year: '2023', posted: '2,38,989', placed: '38,313', rate: '16%' },
  { year: '2024', posted: '2,70,628', placed: '51,822', rate: '19%' },
  { year: '2025', posted: '1,20,452', placed: '21,700', rate: '18%' },
];

const Achievements = () => {
  const [achievements, setAchievements] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('awards');

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const response = await api.get('/cms/achievements');
      setAchievements(response.data.achievements || {});
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-slate-50 to-white flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Our Inspiring Achievements
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              We are proud of our accomplishments as we celebrate our success as a team
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Category Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="flex flex-wrap justify-center gap-2 mb-8 bg-transparent h-auto">
              {categories.map((cat) => (
                <TabsTrigger
                  key={cat.id}
                  value={cat.id}
                  data-testid={`achievements-tab-${cat.id}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 data-[state=active]:bg-teal-600 data-[state=active]:text-white data-[state=active]:border-teal-600"
                >
                  <cat.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{cat.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map((cat) => (
              <TabsContent key={cat.id} value={cat.id}>
                {(achievements[cat.id] || []).length === 0 ? (
                  <div className="text-center py-12">
                    <Image className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No items in this category yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {(achievements[cat.id] || []).map((item, index) => (
                      <Card
                        key={item.id || index}
                        className="overflow-hidden border-slate-200 hover:shadow-lg transition-shadow group"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-medium text-slate-700 text-center line-clamp-2">{item.title}</p>
                          {item.description && (
                            <p className="text-xs text-slate-500 text-center mt-1 line-clamp-2">{item.description}</p>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>

          {/* Yearly Statistics */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              <TrendingUp className="w-8 h-8 inline-block mr-2 text-teal-600" />
              Year-wise Progress
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-teal-600 text-white">
                    <th className="px-6 py-3 text-left font-semibold">Year</th>
                    <th className="px-6 py-3 text-right font-semibold">Jobs Posted</th>
                    <th className="px-6 py-3 text-right font-semibold">Total Placements</th>
                    <th className="px-6 py-3 text-right font-semibold">Success Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyStats.map((stat, index) => (
                    <tr key={stat.year} className={`${index % 2 === 0 ? 'bg-white' : 'bg-teal-50'} border-b border-slate-100`}>
                      <td className="px-6 py-3 font-semibold text-slate-900">{stat.year}</td>
                      <td className="px-6 py-3 text-right text-slate-700">{stat.posted}</td>
                      <td className="px-6 py-3 text-right text-slate-700">{stat.placed}</td>
                      <td className="px-6 py-3 text-right">
                        <span className="inline-block px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">{stat.rate}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Achievements;
